import React from 'react';
import { motion } from 'framer-motion';

const CaseStudySlide = ({ 
  title, 
  titleHighlight,
  image, 
  imageType = 'cover', // 'cover' | 'logo' | 'portrait'
  logoScale = 1, // Scale factor for logo (default 1 = 80% width)
  stats, 
  description, 
  roiCards 
}) => {
  return (
    <div className="h-full w-full bg-slate-50 flex items-center justify-center p-6 md:p-10 relative overflow-hidden">
      <div className="max-w-7xl w-full h-full flex flex-col">
        {/* Header */}
        <motion.div
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="text-center mb-4 md:mb-6"
        >
            <h2 className="text-3xl md:text-5xl font-bold text-slate-900">
                {title} <span className="text-blue-600">{titleHighlight}</span>
            </h2>
        </motion.div>

        {/* Content Grid */}
        <div className="flex-1 grid grid-cols-2 gap-6 lg:gap-12 items-center min-h-0">

            {/* Left: Image */}
            <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
                className={`h-full max-h-[420px] w-full rounded-3xl overflow-hidden relative group ${
                    imageType === 'logo' ? 'bg-gradient-to-br from-white via-slate-50 to-slate-100 flex items-center justify-center border border-slate-200 shadow-2xl' : ''
                } ${imageType === 'portrait' ? 'bg-gradient-to-br from-blue-50 via-white to-slate-100 flex items-center justify-center border border-blue-100 shadow-2xl' : ''
                } ${imageType === 'cover' ? 'shadow-2xl' : ''}`}
            >
                {imageType === 'logo' && (
                   <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#3b82f6_1px,transparent_1px)] [background-size:16px_16px]" />
                )}

                {imageType === 'portrait' ? (
                    <>
                        {/* Background pattern */}
                        <div className="absolute inset-0 opacity-[0.07] bg-[radial-gradient(#3b82f6_1px,transparent_1px)] [background-size:20px_20px]" />
                        {/* Decorative corner accents */}
                        <div className="absolute top-4 left-4 w-16 h-16 border-t-2 border-l-2 border-blue-300 rounded-tl-2xl opacity-40" />
                        <div className="absolute bottom-4 right-4 w-16 h-16 border-b-2 border-r-2 border-blue-300 rounded-br-2xl opacity-40" />
                        {/* Photo */}
                        <img
                            src={image}
                            alt="Case Study"
                            className="relative z-10 w-[75%] h-[80%] object-cover rounded-2xl shadow-xl border-4 border-white transition-transform duration-700 group-hover:scale-105"
                        />
                    </>
                ) : (
                    <img
                        src={image}
                        alt="Case Study"
                        style={imageType === 'logo' ? { width: `${80 * logoScale}%`, height: `${80 * logoScale}%` } : {}}
                        className={`
                            ${imageType === 'logo'
                                ? 'object-contain z-10 p-8 transition-transform duration-700 group-hover:scale-105'
                                : 'w-full h-full object-cover transition-transform duration-700 group-hover:scale-105'
                            }
                        `}
                    />
                )}

                {imageType === 'cover' && (
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
                )}
            </motion.div>

            {/* Right: Info */}
            <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 }}
                className="flex flex-col justify-center space-y-4"
            >
                {/* Stats Grid */}
                <div className="grid grid-cols-1 gap-y-1 text-lg">
                    {stats.map((stat, i) => (
                        <div key={i} className="flex gap-2">
                            <span className="font-bold text-slate-900">{stat.label}:</span>
                            <span className="text-slate-700">{stat.value}</span>
                        </div>
                    ))}
                </div>

                {/* Description */}
                <div className="text-base text-slate-600 leading-relaxed">
                    {description}
                </div>

                {/* ROI Cards */}
                <div className="grid grid-cols-2 gap-3">
                    {roiCards.map((card, i) => (
                        <div key={i} className="bg-white p-4 rounded-2xl shadow-lg border border-slate-100 flex flex-col justify-center">
                            <div className="text-xs text-slate-500 font-semibold mb-1">{card.label}</div>
                            <div className="text-2xl lg:text-3xl font-bold text-slate-900 mb-0.5">{card.value}</div>
                            <div className="text-xs text-slate-400">{card.subtext}</div>
                        </div>
                    ))}
                </div>
            </motion.div>
        </div>
      </div>
    </div>
  );
};

export default CaseStudySlide;
